import classnames from "classnames";
import useScreenSize from "hoc/useScreenSize";
import MagnifyingGlassPlusIcon from "icons/MagnifyingGlassPlusIcon";
import { useTranslation } from "next-i18next";
import {
  CSSProperties, MouseEvent, TouchEvent, useEffect, useRef, useState,
} from "react";
import { createPortal } from "react-dom";
import styles from "./magnify-image.module.scss";

interface Props {
  src: string;
  zoomSrc: string;
  alt: string;
  imgStyle?: CSSProperties;
  imgClassName?: string;
  placement?: "beside" | "over";
  portalEl?: HTMLElement | null;
  zoomScale?: number;
  magnifiedContainerStyle?: CSSProperties;
  onLoad?: (el: HTMLImageElement) => void;
  onError?: () => void;
  onZoomError?: () => void;
}

function MagnifyImage({
  src,
  zoomSrc,
  alt,
  imgStyle,
  imgClassName = "",
  placement = "over",
  portalEl = null,
  zoomScale = 3,
  magnifiedContainerStyle = {},
  onLoad,
  onError,
  onZoomError,
}: Props) {
  const [, , isDesktop] = useScreenSize();
  const { t } = useTranslation("product");

  const [isZooming, setIsZooming] = useState(false);
  const isZoomingRef = useRef(false);
  const [mousePos, setMousePos] = useState({ x: 0.5, y: 0.5 });
  const [imageError, setImageError] = useState(false);
  const imageRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const touchStartPos = useRef({ x: 0, y: 0 });

  const isBeside = placement === "beside";
  const showZoom = isZooming && !imageError;

  useEffect(() => {
    isZoomingRef.current = isZooming;
  }, [isZooming]);

  // Non-passive touchmove so preventDefault can block page scroll while zooming.
  // React's synthetic listeners are passive by default, making preventDefault a no-op.
  useEffect(() => {
    if (isDesktop) return undefined;
    const el = containerRef.current;
    if (!el) return undefined;

    function onNativeTouchMove(e: TouchEvent) {
      if (!isZoomingRef.current || !e.touches[0] || !imageRef.current) return;
      e.preventDefault();
      const rect = imageRef.current.getBoundingClientRect();
      setMousePos({
        x: Math.min(1, Math.max(0, (e.touches[0].clientX - rect.left) / rect.width)),
        y: Math.min(1, Math.max(0, (e.touches[0].clientY - rect.top) / rect.height)),
      });
    }

    el.addEventListener("touchmove", onNativeTouchMove, { passive: false });
    return () => el.removeEventListener("touchmove", onNativeTouchMove);
  }, [isDesktop]);

  function updateMousePos(clientX: number, clientY: number) {
    if (!imageRef.current) return;
    const rect = imageRef.current.getBoundingClientRect();
    setMousePos({
      x: Math.min(1, Math.max(0, (clientX - rect.left) / rect.width)),
      y: Math.min(1, Math.max(0, (clientY - rect.top) / rect.height)),
    });
  }

  function handleMouseMove(e: MouseEvent<HTMLDivElement>) { updateMousePos(e.clientX, e.clientY); }
  function handleMouseEnter() { setIsZooming(true); }
  function handleMouseLeave() { setIsZooming(false); }

  function handleTouchStart(e: TouchEvent) {
    if (!e.touches[0]) return;
    const { clientX, clientY } = e.touches[0];
    touchStartPos.current = { x: clientX, y: clientY };
    longPressTimer.current = setTimeout(() => {
      isZoomingRef.current = true;
      setIsZooming(true);
      updateMousePos(clientX, clientY);
    }, 500);
  }

  // Cancels the long-press timer if the finger moves before zoom activates.
  // Tracking while zooming is handled by the native listener above.
  function handleTouchMove(e: TouchEvent) {
    if (!e.touches[0] || isZoomingRef.current) return;
    const { clientX, clientY } = e.touches[0];
    const dx = Math.abs(clientX - touchStartPos.current.x);
    const dy = Math.abs(clientY - touchStartPos.current.y);
    if (dx > 10 || dy > 10) {
      if (longPressTimer.current !== null) clearTimeout(longPressTimer.current);
    }
  }

  function handleTouchEnd() {
    if (longPressTimer.current !== null) clearTimeout(longPressTimer.current);
    isZoomingRef.current = false;
    setIsZooming(false);
  }

  // ── Zoom geometry ─────────────────────────────────────────────────────────

  const displayedW = imageRef.current?.clientWidth ?? 0;
  const displayedH = imageRef.current?.clientHeight ?? 0;

  // Lens represents the region of the small image that's visible in the zoom panel.
  // Portal is 1.3× the image; overlay is 1× — the lens must match the active panel.
  const zoomPanelScale = isBeside ? 1.3 : 1.0;
  const lensW = displayedW > 0 ? (displayedW * zoomPanelScale) / zoomScale : 0;
  const lensH = displayedH > 0 ? (displayedH * zoomPanelScale) / zoomScale : 0;

  const lensLeft = displayedW > 0
    ? Math.max(0, Math.min(displayedW - lensW, mousePos.x * displayedW - lensW / 2))
    : 0;
  const lensTop = displayedH > 0
    ? Math.max(0, Math.min(displayedH - lensH, mousePos.y * displayedH - lensH / 2))
    : 0;

  const bgXPercent = displayedW > lensW ? (lensLeft / (displayedW - lensW)) * 100 : 50;
  const bgYPercent = displayedH > lensH ? (lensTop / (displayedH - lensH)) * 100 : 50;

  // Mobile: center the visible strip on the finger (direct f*100% mapping is off-center).
  const mobileBgX = Math.max(0, Math.min(100, ((mousePos.x * zoomScale - 0.5) / (zoomScale - 1)) * 100));
  const mobileBgY = Math.max(0, Math.min(100, ((mousePos.y * zoomScale - 0.5) / (zoomScale - 1)) * 100));

  const bgPos = isDesktop ? `${bgXPercent}% ${bgYPercent}%` : `${mobileBgX}% ${mobileBgY}%`;

  // ── Dark lens panels (desktop only) ──────────────────────────────────────

  const darkPanels = showZoom && isDesktop && lensW > 0 ? (
    <div
      className={styles.dark_lens}
      style={{
        left: lensLeft, top: lensTop, width: lensW, height: lensH,
      }}
    />
  ) : null;

  // ── Zoom panels ───────────────────────────────────────────────────────────

  const sharedZoomStyle: CSSProperties = {
    backgroundImage: `url(${zoomSrc})`,
    backgroundPosition: bgPos,
  };

  // Portal uses pixel bgSize so the zoom level stays consistent regardless of portal dimensions.
  const portalBgSize = displayedW > 0
    ? `${displayedW * zoomScale}px ${displayedH * zoomScale}px`
    : `${zoomScale * 100}%`;
  const portalW = displayedW > 0 ? displayedW * 1.3 : 400;
  const portalH = displayedH > 0 ? displayedH * 1.3 : 400;

  const portalPanel = showZoom && isBeside && portalEl
    ? createPortal(
      <div
        className={styles.portal_panel}
        style={{
          width: portalW, height: portalH, backgroundSize: portalBgSize, ...sharedZoomStyle, ...magnifiedContainerStyle,
        }}
      />,
      portalEl,
    )
    : null;

  const overlayPanel = showZoom && !isBeside
    ? (
      <div
        className={styles.overlay_panel}
        style={{ backgroundSize: `${zoomScale * 100}%`, ...sharedZoomStyle }}
      />
    )
    : null;

  return (
    <div
      ref={containerRef}
      className={classnames(styles.container, { [styles.desktop]: isDesktop })}
      style={showZoom && !isBeside ? magnifiedContainerStyle : undefined}
      onMouseMove={isDesktop ? handleMouseMove : undefined}
      onMouseEnter={isDesktop ? handleMouseEnter : undefined}
      onMouseLeave={isDesktop ? handleMouseLeave : undefined}
      onTouchStart={!isDesktop ? handleTouchStart : undefined}
      onTouchMove={!isDesktop ? handleTouchMove : undefined}
      onTouchEnd={!isDesktop ? handleTouchEnd : undefined}
      onTouchCancel={!isDesktop ? handleTouchEnd : undefined}
      onContextMenu={!isDesktop ? (e) => e.preventDefault() : undefined}
    >
      <img
        ref={imageRef}
        src={src}
        alt={alt}
        className={classnames(styles.product_img, imgClassName)}
        style={imgStyle}
        onLoad={(e) => onLoad?.(e.target as HTMLImageElement)}
        onError={() => { setImageError(true); onError?.(); }}
        draggable={false}
      />
      <img src={zoomSrc} className={styles.hidden_preload_img} alt="" onError={onZoomError} />
      {!isZooming && (
        <div className={styles.zoom_hint}>
          <MagnifyingGlassPlusIcon />
          {isDesktop ? t("Hover to Zoom") : t("Long press to zoom")}
        </div>
      )}
      {darkPanels}
      {overlayPanel}
      {portalPanel}
    </div>
  );
}

export default MagnifyImage;
